import React, { useState, useEffect, useMemo } from "react";
import ApexCharts from "react-apexcharts";
import { FaHeart, FaPen, FaFire } from "react-icons/fa";

const ProfilePage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const user = useMemo(() => {
    return {
      username: sessionStorage.getItem("username") || "Guest",
      email: sessionStorage.getItem("email") || "Not Available",
    };
  }, []);

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "G");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      if (user.email === "Not Available" || user.username === "Guest") {
        setError("User is not authenticated.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/posts/filter?email=${user.email}&username=${user.username}`
        );

        if (response.ok) {
          const data = await response.json();
          setBlogs(data);
        } else {
          const errorData = await response.json();
          setError(errorData.message || "Error fetching blogs.");
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError("An error occurred while fetching blogs.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [user]);

  const getStatistics = () => {
    const totalLikes = blogs.reduce(
      (sum, blog) => sum + blog.Total_reaction,
      0
    );
    const totalPosts = blogs.length;

    const dates = blogs.map((blog) => new Date(blog.createdAt).toDateString());
    const uniqueDates = [...new Set(dates)].sort();

    let streak = 1;
    let longestStreak = 1;

    for (let i = 1; i < uniqueDates.length; i++) {
      const currentDate = new Date(uniqueDates[i]);
      const prevDate = new Date(uniqueDates[i - 1]);

      if (currentDate - prevDate === 86400000) {
        streak++;
        longestStreak = Math.max(longestStreak, streak);
      } else {
        streak = 1;
      }
    }

    const timeData = uniqueDates.map((date) => {
      const dailyPosts = blogs.filter(
        (blog) => new Date(blog.createdAt).toDateString() === date
      ).length;

      const dailyLikes = blogs
        .filter((blog) => new Date(blog.createdAt).toDateString() === date)
        .reduce((sum, blog) => sum + blog.Total_reaction, 0);

      return { date, dailyPosts, dailyLikes };
    });

    return { totalLikes, totalPosts, longestStreak, timeData };
  };

  const statistics = getStatistics();

  const barChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        columnWidth: "50%",
        distributed: true, // Important: enables per-bar color
      },
    },
    xaxis: {
      categories: ["Total Posts", "Total Likes", "Longest Streak"],
      labels: { style: { colors: "#fff" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: "#fff" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    grid: { show: false },
    dataLabels: {
      enabled: true,
      style: {
        colors: ["#ffffff"],
        fontSize: "14px",
        fontWeight: "bold",
      },
    },

    tooltip: { theme: "dark" },
    labels: {
      colors: "#ffffff",
    },
    colors: ["#6366F1", "#10B981", "#F59E0B"],
    legend: {
      labels: {
        colors: "#ffffff", // Fix for dark mode legend text
      },
    },
  };

  const barChartSeries = [
    {
      name: "Value",
      data: [
        statistics.totalPosts,
        statistics.totalLikes,
        statistics.longestStreak,
      ],
    },
  ];

  const areaChartOptions = {
    chart: { type: "area", toolbar: { show: false } },
    xaxis: {
      type: "category",
      categories: statistics.timeData.map(
        (entry) =>
          new Date(entry.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          }) // e.g., "21 Jul"
      ),
      labels: { style: { colors: "#fff" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: "#fff" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    grid: { show: false },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    tooltip: { theme: "dark" },
    colors: ["#6366F1", "#10B981"],
    legend: {
      labels: {
        colors: "#ffffff", // Fix for dark mode legend text
      },
    },
  };

  const areaChartSeries = [
    {
      name: "Posts",
      data: statistics.timeData.map((entry) => entry.dailyPosts),
    },
    {
      name: "Likes",
      data: statistics.timeData.map((entry) => entry.dailyLikes),
    },
  ];

  return (
    <div className="flex flex-col items-center bg-slate-900 min-h-screen p-6">
      {/* User Info */}
      <div className="flex flex-col items-center w-full max-w-sm mx-auto p-6 mt-6 rounded-lg shadow-lg bg-slate-900">
        <div className="w-24 h-24 flex items-center justify-center animate-fadeIn rounded-full mb-4 shadow-lg bg-gradient-to-br from-blue-500 to-purple-500 transform hover:scale-105 transition-all duration-300">
          <span className="text-3xl font-bold text-white">
            {getInitial(user.username)}
          </span>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-1">
            User Name: {user.username}
          </h2>
          <p className="text-sm font-mono text-green-400">
            Mail ID: {user.email}
          </p>
        </div>
        <div className="w-full h-0.5 mt-5 mb-5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 animate-pulse" />
      </div>

      {/* Stats Summary */}
      {!loading && blogs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-4xl mt-6 text-white">
          <div className="bg-gray-800 p-4 rounded-lg shadow-md flex items-center gap-4">
            <FaPen className="text-blue-400 text-xl" />
            <div>
              <p className="text-sm">Total Posts</p>
              <p className="text-lg font-bold">{statistics.totalPosts}</p>
            </div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg shadow-md flex items-center gap-4">
            <FaHeart className="text-green-400 text-xl" />
            <div>
              <p className="text-sm">Total Likes</p>
              <p className="text-lg font-bold">{statistics.totalLikes}</p>
            </div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg shadow-md flex items-center gap-4">
            <FaFire className="text-yellow-400 text-xl" />
            <div>
              <p className="text-sm">Longest Streak</p>
              <p className="text-lg font-bold">
                {statistics.longestStreak} days
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full max-w-4xl mt-8 flex flex-col gap-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-white">
            <div className="w-12 h-12 border-4 border-t-transparent border-violet-600 rounded-full animate-spin shadow-lg mb-4"></div>
            <p className="text-lg animate-pulse text-violet-500">Loading data...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-white text-center py-10">
            No blog data available yet.
          </div>
        ) : (
          <>
            {/* Bar Chart - Total Stats */}
            <div className="bg-gray-800 shadow-md rounded-lg p-6 relative overflow-hidden w-full">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-teal-500 opacity-10 pointer-events-none rounded-lg"></div>
              <div className="relative">
                <h3 className="text-lg font-bold text-white text-center mb-4">
                  Total Statistics
                </h3>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[600px]">
                    <ApexCharts
                      options={barChartOptions}
                      series={barChartSeries}
                      type="bar"
                      height={350}
                      width="100%"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Area Chart - Posts and Likes Over Time */}
            <div className="bg-gray-800 shadow-md rounded-lg p-6 relative overflow-hidden w-full">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-teal-500 opacity-10 pointer-events-none rounded-lg"></div>
              <div className="relative">
                <h3 className="text-lg font-bold text-white font-mono mb-4 text-center">
                  Posts and Likes Over Time
                </h3>
                <div className="w-full overflow-x-auto sm:overflow-x-visible">
                  <div className="min-w-[600px] sm:min-w-full">
                    <ApexCharts
                      options={areaChartOptions}
                      series={areaChartSeries}
                      type="area"
                      height={350}
                      width="100%"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {error && <div className="mt-6 text-red-500 font-semibold">{error}</div>}
    </div>
  );
};

export default ProfilePage;
